import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ActivityItem, Company, CompanyAssertion, CompanyDocument, Employee, HrCase } from './types';
import { isConnectivityError } from '../lib/apiClient';
import { listCompanies, mapCompany } from '../lib/entities/companies';
import { listEmployees, mapEmployee } from '../lib/entities/employees';
import { listCases, mapCase } from '../lib/entities/cases';
import { listAssertions, mapAssertion } from '../lib/entities/assertions';
import { listDocuments, mapDocument } from '../lib/entities/documents';
import { listActivity, mapActivity } from '../lib/entities/activity';
import { getChecklist, type ChecklistDto } from '../lib/entities/checklist';

/**
 * Which company is active in the app shell, and its data.
 *
 * Portfolio accounts hold several companies; the sidebar switcher flips between
 * them and every page reads its data through this context. Data now loads from
 * the live API (was static data.ts): the portfolio loads once on mount, then the
 * active company's documents/assertions/employees/cases load whenever it changes.
 *
 * THE INVARIANT: nothing here references a tenant id — the bearer token is the
 * identity and the server resolves the tenant.
 */
interface ActiveCompanyValue {
  companies: Company[];
  companyId: string | null;
  company: Company | null;
  documents: CompanyDocument[];
  assertions: CompanyAssertion[];
  employees: Employee[];
  cases: HrCase[];
  activity: ActivityItem[];
  /**
   * The backend-computed compliance checklist for the active company (documents +
   * attestations + summary). Recomputed server-side on every fetch, so it reflects
   * the company's current headcount/type. Null until the first load completes (or if
   * it fails); both the dashboard and the documents page read it from here.
   */
  checklist: ChecklistDto | null;
  /** Portfolio load in flight (first paint). */
  loading: boolean;
  /** Active-company data load in flight (also true while switching companies). */
  companyLoading: boolean;
  error: string | null;
  setCompanyId: (id: string) => void;
  refetchCompanies: () => Promise<void>;
  refetchEmployees: () => Promise<void>;
  refetchCases: () => Promise<void>;
  refetchAssertions: () => Promise<void>;
  refetchDocuments: () => Promise<void>;
  refetchActivity: () => Promise<void>;
  refetchChecklist: () => Promise<void>;
}

const ActiveCompanyContext = createContext<ActiveCompanyValue | null>(null);

const STORAGE_KEY = 'reeve.activeCompanyId';
const isBrowser = typeof window !== 'undefined';

function readStoredCompanyId(): string | null {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredCompanyId(id: string | null): void {
  if (!isBrowser) return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode / SSR) — non-fatal.
  }
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function ActiveCompanyProvider({ children }: { children: ReactNode }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyIdState] = useState<string | null>(null);
  const [documents, setDocuments] = useState<CompanyDocument[]>([]);
  const [assertions, setAssertions] = useState<CompanyAssertion[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [cases, setCases] = useState<HrCase[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [checklist, setChecklist] = useState<ChecklistDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const company = useMemo(
    () => companies.find((c) => c.id === companyId) ?? null,
    [companies, companyId],
  );

  // Request-id guard so a slow active-company load can't clobber a newer one
  // when the user switches companies rapidly.
  const companyReqId = useRef(0);

  /** Load the portfolio and reconcile the active company id. */
  const refetchCompanies = useCallback(async () => {
    if (!isBrowser) return;
    setLoading(true);
    try {
      const list = (await listCompanies()).map(mapCompany);
      setCompanies(list);
      setError(null);
      setCompanyIdState((prev) => {
        const stored = readStoredCompanyId();
        if (prev && list.some((c) => c.id === prev)) return prev;
        if (stored && list.some((c) => c.id === stored)) return stored;
        return list[0]?.id ?? null;
      });
    } catch (err) {
      // Do NOT blank the portfolio on a connectivity failure. Clearing it makes every surface show
      // its "add your first company" empty state, so a momentary outage reads as data loss. Keep
      // whatever was last known and just surface the error; ConnectionGate handles a backend that is
      // properly down. A real answer (auth, tenant) still clears, because then the emptiness is true.
      if (isConnectivityError(err)) {
        setError(errorMessage(err, 'Could not reach Reeve. Retrying…'));
      } else {
        setCompanies([]);
        setCompanyIdState(null);
        setError(errorMessage(err, 'Could not load your companies.'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /** Load the active company's documents/assertions/employees/cases. */
  const loadCompanyData = useCallback(async (id: string) => {
    const reqId = ++companyReqId.current;
    setCompanyLoading(true);
    try {
      // The checklist is best-effort so its failure can't blank the stored lists: it resolves to null
      // and the cards fall back to a loading/empty state.
      const [docs, asserts, emps, cs, acts, chk] = await Promise.all([
        listDocuments(id),
        listAssertions(id),
        listEmployees(id),
        listCases(id),
        listActivity(id),
        getChecklist(id).catch(() => null),
      ]);
      if (reqId !== companyReqId.current) return; // superseded by a newer switch
      setDocuments(docs.map(mapDocument));
      setAssertions(asserts.map(mapAssertion));
      setEmployees(emps.map(mapEmployee));
      setCases(cs.map(mapCase));
      setActivity(acts.map(mapActivity));
      setChecklist(chk);
      setError(null);
    } catch (err) {
      if (reqId !== companyReqId.current) return;
      setDocuments([]);
      setAssertions([]);
      setEmployees([]);
      setCases([]);
      setActivity([]);
      setChecklist(null);
      setError(errorMessage(err, 'Could not load this company’s data.'));
    } finally {
      if (reqId === companyReqId.current) setCompanyLoading(false);
    }
  }, []);

  // Load the portfolio once on mount.
  useEffect(() => {
    void refetchCompanies();
  }, [refetchCompanies]);

  // (Re)load active-company data whenever the active company changes.
  useEffect(() => {
    if (!companyId) {
      setDocuments([]);
      setAssertions([]);
      setEmployees([]);
      setCases([]);
      setActivity([]);
      setChecklist(null);
      return;
    }
    void loadCompanyData(companyId);
  }, [companyId, loadCompanyData]);

  const setCompanyId = useCallback((id: string) => {
    writeStoredCompanyId(id);
    setCompanyIdState(id);
  }, []);

  // Granular refetches for create/edit forms: refresh just one list, in place.
  const refetchEmployees = useCallback(async () => {
    if (!companyId) return;
    try {
      const emps = await listEmployees(companyId);
      setEmployees(emps.map(mapEmployee));
    } catch (err) {
      setError(errorMessage(err, 'Could not refresh employees.'));
    }
  }, [companyId]);

  const refetchCases = useCallback(async () => {
    if (!companyId) return;
    try {
      const cs = await listCases(companyId);
      setCases(cs.map(mapCase));
    } catch (err) {
      setError(errorMessage(err, 'Could not refresh cases.'));
    }
  }, [companyId]);

  const refetchAssertions = useCallback(async () => {
    if (!companyId) return;
    try {
      const asserts = await listAssertions(companyId);
      setAssertions(asserts.map(mapAssertion));
    } catch (err) {
      setError(errorMessage(err, 'Could not refresh assertions.'));
    }
  }, [companyId]);

  const refetchDocuments = useCallback(async () => {
    if (!companyId) return;
    try {
      const docs = await listDocuments(companyId);
      setDocuments(docs.map(mapDocument));
    } catch (err) {
      setError(errorMessage(err, 'Could not refresh documents.'));
    }
  }, [companyId]);

  const refetchActivity = useCallback(async () => {
    if (!companyId) return;
    try {
      const acts = await listActivity(companyId);
      setActivity(acts.map(mapActivity));
    } catch (err) {
      setError(errorMessage(err, 'Could not refresh activity.'));
    }
  }, [companyId]);

  // Recompute the checklist in place after an upload/confirm changes what's held. Non-fatal: a failure
  // leaves the last-good checklist showing rather than blanking it.
  const refetchChecklist = useCallback(async () => {
    if (!companyId) return;
    try {
      setChecklist(await getChecklist(companyId));
    } catch (err) {
      setError(errorMessage(err, 'Could not refresh your requirements.'));
    }
  }, [companyId]);

  const value = useMemo<ActiveCompanyValue>(
    () => ({
      companies,
      companyId,
      company,
      documents,
      assertions,
      employees,
      cases,
      activity,
      checklist,
      loading,
      companyLoading,
      error,
      setCompanyId,
      refetchCompanies,
      refetchEmployees,
      refetchCases,
      refetchAssertions,
      refetchDocuments,
      refetchActivity,
      refetchChecklist,
    }),
    [
      companies,
      companyId,
      company,
      documents,
      assertions,
      employees,
      cases,
      activity,
      checklist,
      loading,
      companyLoading,
      error,
      setCompanyId,
      refetchCompanies,
      refetchEmployees,
      refetchCases,
      refetchAssertions,
      refetchDocuments,
      refetchActivity,
      refetchChecklist,
    ],
  );

  return <ActiveCompanyContext.Provider value={value}>{children}</ActiveCompanyContext.Provider>;
}

export function useActiveCompany(): ActiveCompanyValue {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) throw new Error('useActiveCompany must be used inside ActiveCompanyProvider');
  return ctx;
}
